import {
  index,
  integer,
  real,
  sqliteTable,
  text,
} from "drizzle-orm/sqlite-core";

import activitySeedData, {
  type ActivitySeedId,
} from "@/db/seed-data/activities";
import type { MediaAssetSlim } from "@/lib/media";

import { timestamps } from "../columns.helpers";
import { user } from "./auth";

// Enums for profile fields
export const GENDER = ["male", "female", "other"] as const;
export const SKILL_LEVEL = [
  "beginner",
  "intermediate",
  "advanced",
  "expert",
] as const;
export const GOAL = ["fitness", "social", "ranked", "recreation"] as const;

// Sport IDs sourced from the canonical activity seed data
const activityIds = activitySeedData.map((activity) => activity.id);

export const ACTIVITY_IDS = activityIds as unknown as Readonly<
  [ActivitySeedId, ...ActivitySeedId[]]
>;

export type Gender = (typeof GENDER)[number];
export type Goal = (typeof GOAL)[number];
export type ActivityId = ActivitySeedId;
export type ActivitySkill = {
  activityId: ActivityId;
  skillLevel: number;
};
export type ImageData = MediaAssetSlim;

export const profile = sqliteTable(
  "profile",
  {
    userId: text("user_id")
      .primaryKey()
      .references(() => user.id, { onDelete: "cascade" }),

    // Step 1: Basic info
    fullName: text("full_name"),
    gender: text("gender").$type<Gender>(),
    dateOfBirth: text("date_of_birth"), // ISO date string

    // Note: activities and activitySkills are now stored in the profile_activity table

    // Step 4: Images (JSON array of MediaAssetSlim)
    images: text("images", { mode: "json" }).$type<ImageData[]>(),

    // Step 5: Bio and goals
    bio: text("bio"),
    goals: text("goals", { mode: "json" }).$type<Goal[]>(), // JSON: ["fitness", "social", ...]

    // Step 6: Location
    locationDisplayName: text("location_display_name").$type<string>(),
    locationLatitude: real("location_latitude"),
    locationLongitude: real("location_longitude"),
    geoHash: text("geohash"), // store precision 9 geohash

    // Profile completion status
    isComplete: integer("is_complete", { mode: "boolean" })
      .notNull()
      .default(false),
    onboardingStep: integer("onboarding_step").notNull().default(1),
    onboardingCompletedAt: integer("onboarding_completed_at", {
      mode: "timestamp",
    }),
    showInDiscover: integer("show_in_discover", { mode: "boolean" })
      .notNull()
      .default(true),
    showInSearch: integer("show_in_search", { mode: "boolean" })
      .notNull()
      .default(true),
    autoPostEventAttendance: integer("auto_post_event_attendance", {
      mode: "boolean",
    })
      .notNull()
      .default(true),
    autoPostActivityResults: integer("auto_post_activity_results", {
      mode: "boolean",
    })
      .notNull()
      .default(true),
    // When true (default), other users may reshare this user's public posts
    // into their own stories (Instagram-style "Allow sharing to story").
    allowStoryReshare: integer("allow_story_reshare", {
      mode: "boolean",
    })
      .notNull()
      .default(true),
    // WhatsApp-style chat privacy. Both are reciprocal: hide your last seen
    // and you can't see anyone else's; turn off read receipts and you stop
    // seeing other people's too (direct chats only — groups always send).
    // Last seen starts hidden; "Active now" is always visible regardless.
    showLastSeen: integer("show_last_seen", { mode: "boolean" })
      .notNull()
      .default(false),
    sendReadReceipts: integer("send_read_receipts", { mode: "boolean" })
      .notNull()
      .default(true),

    ...timestamps,
  },
  (table) => [
    index("profile_by_location").on(
      table.locationLatitude,
      table.locationLongitude,
    ),
    index("profile_by_completion").on(table.isComplete),
    index("profile_geohash_idx").on(table.geoHash),
    index("profile_discover_visibility_idx").on(table.showInDiscover),
    index("profile_search_visibility_idx").on(table.showInSearch),
    index("profile_auto_post_event_attendance_idx").on(
      table.autoPostEventAttendance,
    ),
    index("profile_auto_post_activity_results_idx").on(
      table.autoPostActivityResults,
    ),
  ],
);
